import { LinearProgress } from '@mui/material';
import Link from '@mui/material/Link';
import { appRoutes } from 'app/app-routes';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { UserActionCodeValues } from 'common/types/user-action-code';
import { Formik } from 'formik';
import { User, UserActionCode, UserAdd_Input, useUsersRegisterMutation } from 'graphql/generated';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import { WsButton } from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsFormBox from 'stories/layout/WsFormBox';
import WsFormInfoContainer from 'stories/layout/WsFormInfoContainer';
import WsPanel from 'stories/layout/WsPanel';
import * as yup from 'yup';

interface FormValues extends Pick<User, 'email' | 'password' | 'firstName' | 'lastName' | 'zipCode'> {
  passwordConfirm: string;
}

const nameMinLen = 2;
const nameMaxLen = 30;
const nameMinError = `Min length is ${nameMinLen}`;
const nameMaxError = `Max length is ${nameMaxLen}`;

const validationSchema = yup.object().shape({
  email: formValidationSchema.email,
  password: formValidationSchema.password,
  passwordConfirm: formValidationSchema.passwordConfirm,
  zipCode: formValidationSchema.zipCode,
  firstName: yup.string().min(nameMinLen, nameMinError).max(nameMaxLen, nameMaxError),
  lastName: yup.string().min(nameMinLen, nameMinError).max(nameMaxLen, nameMaxError),
});

const initialValues: FormValues = {
  email: '',
  firstName: '',
  lastName: '',
  zipCode: '',
  password: '',
  passwordConfirm: '',
};

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => cacheSelectors.user(state));
  const [registerUser] = useUsersRegisterMutation();

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      setSubmitting(true);

      const user_add: UserAdd_Input = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        zipCode: formValues.zipCode,
        password: formValues.password,
        verified: false,
      };

      const { data } = await registerUser({
        variables: { object: user_add },
      });
      setSubmitting(false);
      if (!data) throw new Error('Register user error');
      const result = data.usersRegister;
      if (result.code === UserActionCode.created && result.user) {
        const { __typename, ...newUser } = result.user;
        dispatch(cacheActions.user(newUser));
        if (!result.user.verified) {
          navigate(`${appRoutes.verifyEmail}/${formValues.email}`);
        } else {
          navigate(appRoutes.home);
          dispatch(
            cacheActions.addSnack({
              message: UserActionCodeValues[result.code],
              variant: 'success',
            }),
          );
        }
      } else throw new Error(UserActionCodeValues[result.code]);
    } catch (error: any) {
      setSubmitting(false);
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {props => (
        <WsPanel width="400px" title="Create an Account">
          <form onSubmit={props.handleSubmit}>
            <WsFormInfoContainer>Fields marked with (*) are required.</WsFormInfoContainer>
            <WsFormInfoContainer>
              Passwords must contain at least 8 characters, including at least one uppercase letter, one lowercase
              letter, and one number or special symbol (1,2,3,#,&,%,etc.).
            </WsFormInfoContainer>
            <WsFormBox>
              <WsFormTextField fullWidth id="email" label="Email" type="email" formik={props} />
              <WsFormTextField fullWidth id="firstName" label="First Name" type="text" formik={props} />
              <WsFormTextField fullWidth id="lastName" label="Last Name" type="text" formik={props} />
              <WsFormTextField fullWidth id="zipCode" label="Zip Code" type="text" formik={props} />
              <WsFormTextField fullWidth id="password" label="Password" type="password" formik={props} />
              <WsFormTextField
                fullWidth
                id="passwordConfirm"
                label="Password Confirmation"
                type="password"
                formik={props}
              />
              <WsFlexbox centered>
                <WsFormInfoContainer sx={{ marginTop: 1 }}>
                  {`By creating an account, you agree with our `}
                  <Link href={appRoutes.legal}>Terms of Service</Link>
                </WsFormInfoContainer>
              </WsFlexbox>
              {props.isSubmitting && <LinearProgress />}
              <WsButton label="Create Account" type="submit" disabled={props.isSubmitting} />
            </WsFormBox>
          </form>
        </WsPanel>
      )}
    </Formik>
  );
};

export default Register;
